import { ContactForm, ContactInfos, SectionHeading, type FormField, useText } from '@kkf/design'
import { CONTACT_INFOS } from '../data/contact'
import { useT, type MessageKey } from '../i18n'
import { TURNSTILE_SITE_KEY } from '../data/links'

/**
 * The fields, as keys.
 *
 * Module-level so the array is not rebuilt per render, which means it cannot call `t` —
 * hence keys here and the lookup in the component. `name` is the field's identity and is
 * never translated: it is what a submission is keyed on.
 */
const FIELDS: { name: string; placeholderKey: MessageKey; type?: FormField['type']; colClassName?: string }[] = [
  { name: 'name', placeholderKey: 'form.name' },
  { name: 'email', placeholderKey: 'form.email', type: 'email' },
  { name: 'subject', placeholderKey: 'form.subject', colClassName: 'col-12' },
  { name: 'message', placeholderKey: 'form.message', type: 'textarea', colClassName: 'col-12' },
]

/** The contact page. */
export function ContactPage() {
  const t = useT()
  const s = useText()
  const fields: FormField[] = FIELDS.map(({ placeholderKey, ...field }) => ({
    ...field,
    placeholder: t(placeholderKey),
  }))

  return (
    <>
      {/*
        No banner and no breadcrumb, at the Foundation's request on 2026-09-21.

        Both went together because they were one element: `Breadcrumb` IS the photographic
        band, and the trail sat inside it. Two consequences, and each of them is documented
        elsewhere in this repo as a thing that passes every gate:

        1. **It carried the page's only `<h1>`.** `audit:seo` fails a page with any number
           other than one, so the title moves here as `.ul-plain-page-title` — the class
           that exists for exactly this, beside `.ul-plain-page-top` in `_header.scss`.
        2. **The page now opens pale**, and the header's links are white at rest because
           the usual first section is a dark band. `/contact` is in `header.plainRoutes`
           in `data/shell` as of the same day. Forget that and every link in the nav is
           white on near-white, and nothing anywhere goes red.
      */}
      <section className="ul-inner-contact-intro ul-plain-page-top">
        <div className="ul-container">
          <span className="ul-section-sub-title ul-section-sub-title--2">{t('contact.eyebrow')}</span>
          <h1 className="ul-plain-page-title">{t('page.contact')}</h1>
        </div>
      </section>

      {/* The inbox is printed as written; everything around it is translated. */}
      <ContactInfos
        items={CONTACT_INFOS.map((info) => ({
          icon: info.icon,
          title: s(info.title),
          value: typeof info.value === 'string' ? info.value : s(info.value),
          ...(info.href ? { href: info.href } : {}),
        }))}
      />

      {/* The map was here and is gone — see the note at the foot of `data/contact.ts`. It
          was the template's embed of a zoo in Bangladesh, and a map on a contact page says
          "this is where we are". There is no address to centre a correct one on yet. */}

      {/* CONTACT FORM */}
      <section className="ul-inner-contact ul-section-spacing">
        {/* `subTitle` was `page.contact`, which the `<h1>` above now carries. Saying it
            twice on one page reads as a template, not as a page. */}
        <SectionHeading className="justify-content-center text-center" title={t('contact.title')} />

        <div className="ul-inner-contact-container">
          {/* Both anti-spam measures come with the block — see `ContactForm`. The key is
              unset today, so no widget renders and the form looks exactly as it did. */}
          <ContactForm
            id="contact"
            fields={fields}
            submitLabel={t('contact.eyebrow')}
            centered
            turnstileSiteKey={TURNSTILE_SITE_KEY}
          />
        </div>
      </section>
    </>
  )
}
